import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import errorHandler from "../../../utils/errorHandler";
import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import BTNLoader from "../../../lib/components/BTNLoader";
import * as Yup from "yup";
import userservice from "../../../service/userservice";
import organizationservice from "../../../service/organizationservice";
import AppSelectField from "../../../lib/components/AppSelectField";
import NumberFormatCustom from "../../../lib/components/CurrencyFormat";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import _ from "lodash";
import getDeviceDetails from "../../../utils/getDeviceDetails";

/* ============================================================
   VALIDATION SCHEMA
============================================================ */
const schema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  organizationName: Yup.string().required("Organization Name is required").min(3, "Organization Name must be at least 3 characters"),
  organizationType: Yup.string()
    .oneOf(["BUSINESS", "GOVERNMENT"], "Select a valid Organization Type")
    .required("Organization Type is required"),
  planId: Yup.string().required("Subscription Plan is required"),
  phone: Yup.string()
    .required("Phone is required")
    .min(8, "Phone number must be at least 8 characters")
    .max(15, "Phone number must not exceed 15 characters")
    .matches(/^[0-9+-]*$/, "Phone number can only contain digits, +, and -"),
  platform: Yup.string().required("Platform is required"),
  onboardingChannel: Yup.string().when("platform", {
    is: "FIRS_EXTENSION",
    then: (schema) => schema.oneOf(["PORTAL", "API"], "Select a valid Onboarding Channel").required("Onboarding Channel is required"),
    otherwise: (schema) => schema.strip(),
  }),
  accessMode: Yup.string().when("platform", {
    is: "FIRS_EXTENSION",
    then: (schema) => schema.oneOf(["TEST", "LIVE"], "Select a valid Access").required("Access is required"),
    otherwise: (schema) => schema.strip(),
  }),
  billingInterval: Yup.string().required("Subscription Period is required"),
  amount: Yup.number().typeError("Enter a valid amount").required("Setup Cost is required"),
  paymentDate: Yup.date().typeError("Enter a valid date").required("Payment Date is required"),
  email: Yup.string().trim().email("Invalid email").required("Email is required"),
});

/* ============================================================
   DEFAULT VALUES
============================================================ */

const ORGANIZATION_TYPES = [
  { label: "Business", value: "BUSINESS" },
  { label: "Government Agency", value: "GOVERNMENT" },
];

const ONBOARDING_CHANNELS = [
  { label: "Transmission Portal", value: "PORTAL" },
  { label: "API", value: "API" },
];

const ACCESS_MODES = [
  { label: "Test mode only", value: "TEST" },
  { label: "Test and Live", value: "LIVE" },
];

const defaultValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  organizationName: "",
  organizationType: "BUSINESS",
  planId: "",
  platform: "",
  billingInterval: "",
  amount: "",
  paymentDate: null,
  onboardingChannel: "PORTAL",
  accessMode: "LIVE",
};

/* ============================================================
   COMPONENT
============================================================ */
const AddNewUser = ({ isOpen, onClose, refetch }) => {
  const [step, setStep] = React.useState(1);

  const formHook = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues,
    criteriaMode: "all",
  });

  const { control, handleSubmit, reset, watch, formState } = formHook;
  const { errors, isValid } = formState;

  const clientPlatform = {
    type: "WEB",
    device: `${getDeviceDetails()["os.name"]} · ${getDeviceDetails()["browser.name"]}`,
  };

  /* ============================================================
     STEP VALIDATION LOGIC
  ============================================================ */

  const step1Fields = ["firstName", "lastName", "organizationName", "organizationType", "email", "phone"];
  const step2Fields = ["planId", "platform", "billingInterval", "amount", "paymentDate"];

  // Step 1 valid if all required fields are non-empty and no errors
  const isStep1Valid = step === 1 && step1Fields.every((f) => !!watch(f)) && !step1Fields.some((f) => errors[f]);
  const isStep2Valid = step === 2 && step2Fields.every((f) => !!watch(f)) && !step2Fields.some((f) => errors[f]);

  /* ============================================================
     QUERIES
  ============================================================ */
  const plansQuery = useQuery({
    retry: false,
    enabled: isOpen,
    queryKey: ["subscription-plans"],
    queryFn: organizationservice.getSubscriptionPlans,
  });

  const mutation = useMutation({
    mutationFn: (data) => userservice.create(data),
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      reset(defaultValues);
      setStep(1);
      toast.success("User added successfully.");
      refetch();
      onClose();
    },
  });

  const handleClose = () => {
    reset(defaultValues);
    setStep(1);
    onClose();
  };

  const { isPending } = mutation;
  const plans = plansQuery.data?.data?.data || [];
  const selectedPlanId = watch("planId");
  const selectedInterval = watch("billingInterval");
  const selectedPlatform = watch("platform");

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  const isCustomPlan = typeof selectedPlan?.type === "string" && selectedPlan.type.toUpperCase() === "CUSTOM";

  const sourceIntervals = selectedPlan?.pricing || {};

  const intervalOptions = selectedPlan
    ? Object.entries(selectedPlan.pricing || {})
        .filter(([_, v]) => {
          if (!v?.isActive) return false;
          if (isCustomPlan) {
            return Number(v.amount) > 0;
          }
          return true;
        })
        .map(([key, value]) => ({
          label: _.capitalize(key),
          value: value.interval,
        }))
    : [];

  // Compute recurrent amount
  // const computeRecurrentAmount = React.useCallback(
  //   (intervalKey) => {
  //     if (!selectedPlan) return "";
  //     const norm = intervalKey.toLowerCase();
  //     if (!selectedPlan.isCustom) {
  //       // For non-custom plans, recurrent cost is ALWAYS zero
  //       return 0;
  //     }
  //     // For custom plans → use recurrentCost
  //     return selectedPlan?.recurrentCost?.[norm]?.amount || 0;
  //   },
  //   [selectedPlan]
  // );
  //   React.useEffect(() => {
  //   if (!selectedPlanId || !selectedInterval) return;

  //   const amount = computeRecurrentAmount(selectedInterval);
  //   formHook.setValue("recurrentCost", amount, { shouldValidate: true });
  // }, [selectedPlanId, selectedInterval, computeRecurrentAmount]);
  // React.useEffect(() => {
  //       if (!selectedPlan) return;
  //       const volume = selectedPlan?.invoiceVolume;
  //       if (volume && Number(volume) > 0) {
  //         formHook.setValue("invoiceVolume", String(volume), { shouldValidate: true });
  //       } else {
  //         formHook.setValue("invoiceVolume", "0", { shouldValidate: true });
  //       }
  //   }, [selectedPlan]);

  React.useEffect(() => {
    formHook.setValue("billingInterval", "", { shouldValidate: true });
  }, [selectedPlanId]);

  const onboardingChannel = formHook.watch("onboardingChannel");
  const accessModes = onboardingChannel === "API" ? ACCESS_MODES : ACCESS_MODES.filter((mode) => mode.value === "LIVE");

  React.useEffect(() => {
    formHook.setValue("accessMode", onboardingChannel === "API" ? "TEST" : "LIVE", { shouldValidate: true });
  }, [onboardingChannel]);

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      PaperProps={{
        className: "w-100",
        component: "form",
        onSubmit: handleSubmit((data) => {
          const { accessMode, ...rest } = data;
          const payload = { ...rest, clientPlatform };
          if (payload.platform !== "FIRS_EXTENSION") delete payload.onboardingChannel;
          else payload.isLiveMode = accessMode === "LIVE";
          mutation.mutate(payload);
        }),
      }}
    >
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
        <div className="fs-5 fw-medium">New User</div>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </div>

      <Divider className="bg-secondary-subtle" />

      {/* CONTENT */}
      <DialogContent className="h-100 overflow-auto">
        {/* ============================================================
            STEP 1
        ============================================================ */}
        {step === 1 && (
          <>
            <Grid container spacing={3}>
              {/* FIRST NAME */}
              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    First Name <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter First Name"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.firstName?.message}
                        error={!!errors.firstName}
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* LAST NAME */}
              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Last Name"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.lastName?.message}
                        error={!!errors.lastName}
                      />
                    )}
                  />
                </div>
              </Grid>
            </Grid>

            {/* EMAIL */}
            <div className="mb-4">
              <label>
                Email <span className="text-danger">*</span>
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder="Enter email address"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    helperText={errors.email?.message}
                    error={!!errors.email}
                  />
                )}
              />
            </div>

            {/* PHONE */}
            <div className="mb-4">
              <label>
                Phone Number <span className="text-danger">*</span>
              </label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter phone number"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    helperText={errors.phone?.message}
                    error={!!errors.phone}
                  />
                )}
              />
            </div>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Organization Name <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="organizationName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter organization name"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.organizationName?.message}
                        error={!!errors.organizationName}
                      />
                    )}
                  />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Organization Type <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="organizationType"
                    control={control}
                    render={({ field }) => (
                      <AppSelectField
                        value={field.value}
                        placeholder="Select Organization Type"
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.organizationType?.message}
                        error={!!errors.organizationType}
                        items={ORGANIZATION_TYPES}
                      />
                    )}
                  />
                </div>
              </Grid>
            </Grid>
          </>
        )}

        {/* ============================================================
            STEP 2
        ============================================================ */}
        {step === 2 && (
          <>
            <Grid container spacing={3}>
              {/* PLAN */}
              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Subscription Plan <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="planId"
                    control={control}
                    render={({ field }) => (
                      <AppSelectField
                        value={field.value}
                        placeholder="Select Subscription Plan"
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.planId?.message}
                        error={!!errors.planId}
                        items={plans.map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))}
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* PLATFORM */}
              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Platform <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="platform"
                    control={control}
                    render={({ field }) => (
                      <AppSelectField
                        value={field.value}
                        placeholder="Select Platform"
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.platform?.message}
                        error={!!errors.platform}
                        items={["MAIN", "FIRS_EXTENSION"].map((item) => ({
                          label: _.capitalize(item.split("_").join(" ")),
                          value: item,
                        }))}
                      />
                    )}
                  />
                </div>
              </Grid>
            </Grid>

            {selectedPlatform === "FIRS_EXTENSION" && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <div className="mb-4">
                    <label>
                      Onboarding Channel <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="onboardingChannel"
                      control={control}
                      render={({ field }) => (
                        <AppSelectField
                          value={field.value}
                          placeholder="Select Onboarding Channel"
                          onChange={(e) => field.onChange(e.target.value)}
                          helperText={errors.onboardingChannel?.message}
                          error={!!errors.onboardingChannel}
                          items={ONBOARDING_CHANNELS}
                        />
                      )}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} md={6}>
                  <div className="mb-4">
                    <label>
                      Access <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="accessMode"
                      control={control}
                      render={({ field }) => (
                        <AppSelectField
                          value={field.value}
                          placeholder="Select Access"
                          onChange={(e) => field.onChange(e.target.value)}
                          helperText={errors.accessMode?.message}
                          error={!!errors.accessMode}
                          items={accessModes}
                        />
                      )}
                    />
                  </div>
                </Grid>
              </Grid>
            )}

            {/* <Grid container spacing={3}> */}
            {/* <Grid item xs={12} md={6}>
                  <div className="mb-4">
                    <label>
                      ERP <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="erp"
                      control={control}
                      render={({ field }) => (
                        <>
                          <AppSelectField
                            value={field.value}
                            placeholder="Select ERP"
                            onChange={(e) => field.onChange(e.target.value)}
                            helperText={errors.erp?.message}
                            error={!!errors.erp}
                            items={[
                              "Tally Prime",
                              "Microsoft Dynamics",
                              "SAP",
                              "Quickbooks",
                              "Zero",
                              "Others",
                            ].map((item) => ({
                              label: item,
                              value: item,
                            }))}
                          />
                          {field.value === "Other" && (
                            <Controller
                              name="erpOther"
                              control={control}
                              render={({ field: otherField }) => (
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Enter ERP Name"
                                  value={otherField.value || ""}
                                  onChange={(e) => otherField.onChange(e.target.value)}
                                  error={!!errors.erpOther}
                                  helperText={errors.erpOther?.message}
                                />
                              )}
                            />
                          )}
                        </>
                      )}
                    />
                    {errors.erpOther && (
                      <p className="text-danger mt-1">{errors.erpOther.message}</p>
                    )}
                  </div>
                </Grid> */}

            {/* Invoice Volume */}
            {/* <Grid item xs={12} md={6}>
                  <div className="mb-4">
                    <label>
                      Max. Invoice Volume <span className="text-danger">*</span>
                    </label>
                    
                    <Controller
                      name="invoiceVolume"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          size="small"
                          value={field.value}
                          InputProps={{
                            readOnly: true,
                          }}
                          helperText={errors.invoiceVolume?.message}
                          error={!!errors.invoiceVolume}
                        />
                      )}
                    />

                  </div>
                </Grid> */}
            {/* </Grid> */}

            {/* Should now take the full row */}
            <Grid container spacing={3}>
              {/* Setup Cost */}
              <Grid item xs={12}>
                {/* BILLING INTERVAL */}
                <div className="mb-4">
                  <label>
                    Subscription Period <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="billingInterval"
                    control={control}
                    render={({ field }) => (
                      <AppSelectField
                        value={field.value}
                        placeholder="Select Period"
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.billingInterval?.message}
                        error={!!errors.billingInterval}
                        items={intervalOptions}
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* Date Added */}
              {/* <Grid item xs={12} md={6}>
                    <div className="mb-4">
                      <label>
                        Date Added <span className="text-danger">*</span>
                      </label>
                      <Controller
                        name="paymentDate"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            size="small"
                            type="date"
                            fullWidth
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            helperText={errors.paymentDate?.message}
                            error={!!errors.paymentDate}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        )}
                      />
                    </div>
                  </Grid> */}
            </Grid>

            <Grid container spacing={3}>
              {/* AMOUNT */}
              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Setup Cost <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        size="small"
                        fullWidth
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.amount?.message}
                        error={!!errors.amount}
                        InputProps={{
                          inputComponent: NumberFormatCustom,
                          startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        }}
                      />
                    )}
                  />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Date Added <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="paymentDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        size="small"
                        type="date"
                        fullWidth
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.paymentDate?.message}
                        error={!!errors.paymentDate}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </div>
              </Grid>

              {/* RECURRENT COST */}
              {/* <Grid item xs={12} md={6}>
                <div className="mb-4">
                  <label>
                    Recurrent Cost <span className="text-danger">*</span>
                  </label>
                 <Controller
                    name="recurrentCost"
                    control={control}
                    render={({ field }) => (
                      
                      <TextField
                        size="small"
                        fullWidth
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        helperText={errors.recurrentCost?.message}
                        error={!!errors.recurrentCost}
                        InputProps={{
                          readOnly: true,
                          inputComponent: NumberFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">₦</InputAdornment>
                          ),
                        }}
                      />

                    )}
                  />
                </div>
              </Grid> */}
            </Grid>
          </>
        )}
      </DialogContent>

      {/* ============================================================
          DOT INDICATOR
      ============================================================ */}
      {/* STEP INDICATOR — Bottom Left */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "0 24px",
          paddingBottom: "8px",
        }}
      >
        {/* Step 1 Line */}
        <div
          style={{
            width: "35px",
            height: "6px",
            borderRadius: "4px",
            backgroundColor: step === 1 ? "#1A73E8" : "#D0D5DD",
            transition: "0.3s",
          }}
        />

        {/* Step 2 Line */}
        <div
          style={{
            width: "20px",
            height: "6px",
            borderRadius: "4px",
            backgroundColor: step === 2 ? "#1A73E8" : "#D0D5DD",
            transition: "0.3s",
          }}
        />
      </div>

      {/* ============================================================
          ACTION BUTTONS
      ============================================================ */}
      <DialogActions sx={{ padding: "16px 24px" }}>
        {/* STEP 1 BUTTONS */}
        {step === 1 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>

            <Button
              type="button"
              variant="contained"
              color="secondary"
              disabled={!isStep1Valid} // now disabled on first render
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          </>
        )}

        {/* STEP 2 BUTTONS */}
        {step === 2 && (
          <>
            <Button
              type="button"
              variant="outlined"
              onClick={() => setStep(1)}
              sx={{
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": { borderColor: "#98A2B3" },
              }}
            >
              Back
            </Button>

            <Button
              type="submit"
              disabled={!isStep2Valid || isPending} // now disabled on first render
              startIcon={<BTNLoader isLoading={isPending} />}
              variant="contained"
              color="secondary"
            >
              Add User
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddNewUser;
